import { Button, Divider } from 'antd';
import { OrderStatus } from './OrderStatus';
import { OrderProductList } from './OrderProductList';
import { getOrderTotalAmount } from '../../utils/order';
import { OrderLogistics } from './OrderLogistics';
import { OrderRow } from './OrderRow';
import { useTranslate } from '@tolgee/react';
import { useOrderNote } from 'hooks/business/chat/useOrderNote';
import { OrderNote } from './OrderNote';
import { useOrderSendButtonInfo } from 'hooks/business/chat/useOrder';
import { TradeWithQueryParams } from 'hooks/business/chat/useOrderList';
import { OrderDrawer } from '../OrderDrawer';
import { useMemoizedFn, useToggle } from 'ahooks';
import { useTradeLogisticsBlocks } from 'views/Conversation/Sider/useTradeLogisticsBlocks';
import { OrderCancel } from './OrderCancel';
import useTradeCancelInfo from 'views/Conversation/Sider/hooks/useTradeCancelInfo';
import { useOrderUpdate } from 'hooks/business/chat/useOrderUpdate';
import { OrderReturn } from './OrderReturn';
import { useMemo, useState } from 'react';
import { useOrderReturn } from 'hooks/business/chat/useOrderReturn';
import { OrderCardProvider } from './context';
import { OrderDrawerEntryParams } from 'components/LightConversation/types';
import { DefaultButton } from '../DefaultButton';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import CancelOrder from 'views/Conversation/Sider/TiktokProactive/CancelOrder';
import Refund from 'views/Conversation/Sider/TiktokProactive/Refund';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { TradeListResponse } from 'services/api/conversation';
import { ApiQueryKey } from 'services/config/queryKey';

interface OrderCardProps {
  conversation: Conversation.Conversation;
  order: Conversation.Trade.Vo | TradeWithQueryParams;
  sendable?: boolean;
}

/** 类型守卫：判断 order 是否带有 _queryParams */
const hasQueryParams = (
  order: Conversation.Trade.Vo | TradeWithQueryParams,
): order is TradeWithQueryParams => {
  return '_queryParams' in order;
};

export const OrderCard = (props: OrderCardProps) => {
  const { conversation, order, sendable = true } = props;
  const orderQueryParams = hasQueryParams(order) ? order._queryParams : undefined;
  const {
    platform,
    items,
    tradeStatus,
    externalTradeId,
    createdTimeInMs,
    buyerMemos,
    internalTradeId,
    eligibility,
  } = order as TradeWithQueryParams;
  const { storeId, buyerId, tunnelAccountId, channel } = conversation;
  const { t } = useTranslate();
  const orderTotalAmount = getOrderTotalAmount(order);
  const [orderDrawerVisible, { toggle: toggleOrderDrawerVisible }] = useToggle(false);
  const [entryParams, setEntryParams] = useState<OrderDrawerEntryParams>({});
  const { sendEnabled, sendDisabled, sendHandler } = useOrderSendButtonInfo(
    order,
    conversation,
    sendable,
  );

  const { enable, note, isShopeeIn30daysMemo } = useOrderNote(order);
  const { logisticsInfo } = useTradeLogisticsBlocks(order, { sendable });

  const { data: cancelInfo } = useTradeCancelInfo({ trade: order, storeId });
  const { mutateAsync: updateOrder } = useOrderUpdate({ queryParams: orderQueryParams });

  const onUpdate = useMemoizedFn(() => {
    return updateOrder({
      internalBuyerId: buyerId,
      storeId: storeId,
      tunnelAccountId: tunnelAccountId,
      externalTradeId: externalTradeId,
    });
  });

  const leChatStatus = useMemo(() => {
    const payload = logisticsInfo?.payload;
    if (payload?.kind === 'common') {
      const found = payload?.logisticsList?.find((logistics) => {
        return !!logistics.leChatStatus;
      });
      return found?.leChatStatus;
    }
  }, [logisticsInfo]);

  const { returnItemIdList: returnedItemIds } = useOrderReturn(order);

  const hasReturnedItem = returnedItemIds.length > 0;

  const showNote = enable && !!note;
  const showMoreInfo =
    logisticsInfo || buyerMemos || showNote || cancelInfo.shouldRender || hasReturnedItem;

  const openOrderDrawer = useMemoizedFn((params: OrderDrawerEntryParams) => {
    setEntryParams(params);
    toggleOrderDrawerVisible();
  });

  const queryClient = useQueryClient();

  const onOrderUpdate = useMemoizedFn((order: TradeWithQueryParams) => {
    queryClient.setQueryData<InfiniteData<TradeListResponse>>(
      [ApiQueryKey.OrderList, order._queryParams],
      (old: InfiniteData<TradeListResponse> | undefined) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => {
            return {
              ...page,
              tradeList: page.tradeList?.map((cur: Conversation.Trade.Vo) => {
                if (cur.internalTradeId === order.internalTradeId) {
                  return order;
                } else {
                  return cur;
                }
              }),
            };
          }),
        };
      },
    );
  });

  return (
    <OrderCardProvider value={{ openOrderDrawer }}>
      <div className="px-4 bg-[#FFFFFF] relative">
        <OrderStatus
          tradeStatus={tradeStatus}
          createTime={createdTimeInMs}
          externalTradeId={externalTradeId}
        />
        <div className="mt-3 mb-4 flex items-center gap-3">
          {sendEnabled && (
            <Button type="primary" size="small" disabled={sendDisabled} onClick={sendHandler}>
              {t('trade.send')}
            </Button>
          )}
          {isTiKTokUpperCase(channel) &&
            eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
              lineItemEligibility.some(({ requestType }) => requestType === 'CANCEL'),
            ) && <CancelOrder order={order} storeId={storeId} onUpdate={onOrderUpdate as any} />}
          {isTiKTokUpperCase(channel) &&
            eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
              lineItemEligibility.some(({ requestType }) => requestType === 'REFUND'),
            ) && (
              <Refund
                type="REFUND"
                order={order}
                storeId={storeId}
                onUpdate={onOrderUpdate as any}
              />
            )}
          {isTiKTokUpperCase(channel) &&
            eligibility?.skuEligibility.some(({ lineItemEligibility }) =>
              lineItemEligibility.some(({ requestType }) => requestType === 'RETURN_AND_REFUND'),
            ) && (
              <Refund
                type="RETURN_AND_REFUND"
                order={order}
                storeId={storeId}
                onUpdate={onOrderUpdate as any}
              />
            )}
          <DefaultButton size="small" onClick={() => openOrderDrawer({ entryType: 'order' })}>
            {t('feedback.详情')}
          </DefaultButton>
          {enable && (
            <OrderNote
              isShopeeIn30daysMemo={isShopeeIn30daysMemo}
              internalTradeId={internalTradeId}
              platform={platform}
              content={note}
              queryParams={orderQueryParams}
              editText={t('sidebar.order.note.action.edit')}
              addText={t('sidebar.order.note.action.create')}
            />
          )}
        </div>
        <Divider className="!my-0" />
        <OrderProductList
          conversationId={conversation.conversationId}
          products={items}
          order={order}
          orderTotalAmount={orderTotalAmount}
          leChatStatus={leChatStatus}
          returnedItemIds={returnedItemIds}
        />
        {showMoreInfo && (
          <>
            <Divider className="!my-0" />
            <div className="flex flex-col py-3 gap-3">
              {hasReturnedItem && (
                <OrderReturn storeId={storeId} order={order} onUpdate={onUpdate} />
              )}
              {cancelInfo.shouldRender && (
                <OrderCancel cancelInfo={cancelInfo} onUpdate={onUpdate} />
              )}
              {logisticsInfo && (
                <OrderLogistics
                  statusLabel={logisticsInfo.trackingLabel}
                  data={logisticsInfo.payload}
                  sendable={sendable}
                  conversation={conversation}
                />
              )}
              {showNote && <OrderRow title={t('trade.commentContent')} value={note} />}
              {buyerMemos && <OrderRow title={t('trade.买家备注')} value={buyerMemos} />}
            </div>
          </>
        )}
        <OrderDrawer
          conversation={conversation}
          order={order}
          visible={orderDrawerVisible}
          sendable={sendable}
          onClose={toggleOrderDrawerVisible}
          orderQueryParams={orderQueryParams}
          {...entryParams}
        />
      </div>
    </OrderCardProvider>
  );
};
